import api from './api';

export interface Patient {
  id: number;
  id_number: string | null;
  hm_id: string | null;
  uuid: string | null;
  first_name: string;
  last_name: string;
  full_name: string;
  date_of_birth: string;
  start_medication?: string | null;
  email: string | null;
  phone: string | null;
  status: 'waiting_for_payment' | 'new' | 'intake' | 'titration' | 'finish';
  notes: string | null;
  gender: string | null;
  tenant_id: number;
  clinic_id: number;
  clinic_name: string | null;
  referral_id: number | null;
  referral_name: string | null;
  doctor_id: number | null;
  case_manager_id: number | null;
  nurse_prescriber_id: number | null;
  medication_monitoring_status?: string | null;
  doctor_name: string | null;
  case_manager_name: string | null;
  nurse_prescriber_name: string | null;
  signaturerx_patient_id?: string | null;
  created_at: string;
  updated_at: string;
  last_meeting_date: string | null;
  next_meeting_type_id: number | null;
  next_meeting_start: string | null;
  next_meeting_id: number | null;
  next_meeting_type_name: string | null;
  titration_status: string | null;
  titration_measurement_status: string | null;
  titration_measurement_approval_status: string | null;
  treatment_status: string | null;
  finish_reason: string | null;
  finish_notes: string | null;
  is_test: boolean;
  patient_type_id?: number | null;
  additional_fields?: Record<string, any> | null;
}

export interface PatientCreate {
  first_name: string;
  last_name: string;
  date_of_birth: string;
  start_medication?: string | null;
  email?: string | null;
  phone?: string | null;
  id_number?: string | null;
  gender?: string | null;
  notes?: string | null;
  patient_type_id?: number | null;
  tenant_id: number;
}

export interface PatientUpdate extends Partial<PatientCreate> {
  patient_type_id?: number | null;
  additional_fields?: Record<string, any> | null;
}

export interface PaginatedPatients {
  items: Patient[];
  total: number;
}

export const patientService = {
  async getPatients(
    tenantId: number,
    statusFilters: string[] = [],
    clinicIds: number[] = [],
    skip: number = 0,
    limit: number = 100,
    sortBy?: string,
    sortOrder?: string,
    search?: string,
  ): Promise<PaginatedPatients> {
    const params = new URLSearchParams({ tenant_id: tenantId.toString(), skip: skip.toString(), limit: limit.toString() });
    for (const s of statusFilters) params.append('status', s);
    for (const id of clinicIds) params.append('clinic_id', id.toString());
    if (sortBy) params.append('sort_by', sortBy);
    if (sortOrder) params.append('sort_order', sortOrder);
    if (search) params.append('search', search);
    const response = await api.get(`/patients/?${params}`);
    return response.data;
  },

  async getAllPatients(tenantId: number): Promise<Patient[]> {
    const result = await this.getPatients(tenantId, [], [], 0, 10000);
    return result.items;
  },

  async getPatient(patientId: number): Promise<Patient> {
    const response = await api.get(`/patients/${patientId}`);
    return response.data;
  },

  async createPatient(patientData: PatientCreate): Promise<Patient> {
    const response = await api.post('/patients/', patientData);
    return response.data;
  },

  async updatePatient(patientId: number, patientData: PatientUpdate): Promise<Patient> {
    const response = await api.put(`/patients/${patientId}`, patientData);
    return response.data;
  },

  async finishPatient(patientId: number, data?: { finish_reason?: string; finish_notes?: string }): Promise<void> {
    await api.post(`/patients/${patientId}/finish`, data || {});
  },

  async revivePatient(patientId: number): Promise<void> {
    await api.post(`/patients/${patientId}/revive`);
  },

  async sendEmail(patientId: number, templateId: number): Promise<any> {
    const response = await api.post(`/patients/${patientId}/send-email`, {
      template_id: templateId,
    });
    return response.data;
  },

  async sendSms(patientId: number, templateId: number, content?: string): Promise<any> {
    const response = await api.post(`/patients/${patientId}/send-sms`, {
      template_id: templateId,
      // Only present when the sender edited the text; absent keeps the
      // server's own template rendering.
      ...(content !== undefined ? { content } : {}),
    });
    return response.data;
  },

  async getPatientFields(patientTypeId?: number, tenantId?: number): Promise<Array<{ name: string; label: string; type: string; field_type?: string; options?: Array<{ value: string; label: string }> }>> {
    // tenantId targets a specific tenant's fields (the lead-to-patient mapping
    // screen a system user drives for another tenant); omit it for own-tenant screens.
    const qs = new URLSearchParams();
    if (patientTypeId !== undefined) qs.set('patient_type_id', String(patientTypeId));
    if (tenantId !== undefined) qs.set('tenant_id', String(tenantId));
    const params = qs.toString() ? `?${qs.toString()}` : '';
    const response = await api.get(`/patients/fields${params}`);
    return response.data;
  },

  async getPatientSummaries(patientId: number): Promise<PatientSummaryItem[]> {
    const response = await api.get(`/patients/${patientId}/summaries`);
    return response.data;
  },

  async getTitrationMeasurements(patientId: number): Promise<TitrationMeasurement[]> {
    const response = await api.get(`/patients/${patientId}/titration-measurements`);
    return response.data;
  },

  // ── Titration measurement review actions (Approve Titration Measurements popup) ──
  async approveTitrationMeasurement(patientId: number): Promise<TitrationReviewResult> {
    const response = await api.post(`/patients/${patientId}/titration-measurements/approve`);
    return response.data;
  },

  async rejectTitrationMeasurement(
    patientId: number,
    payload: { reasons: string[]; reason_text?: string | null },
  ): Promise<TitrationReviewResult> {
    const response = await api.post(`/patients/${patientId}/titration-measurements/reject`, payload);
    return response.data;
  },

  async requestTitrationAdditionalInfo(
    patientId: number,
    payload: { options: string[]; info_text?: string | null },
  ): Promise<TitrationReviewResult> {
    const response = await api.post(`/patients/${patientId}/titration-measurements/request-additional-info`, payload);
    return response.data;
  },

  // ── Medication monitoring review (Medication Monitoring Issue popup) ──
  async getMedicationMonitoring(patientId: number): Promise<MedicationMonitoringReview> {
    const response = await api.get(`/patients/${patientId}/medication-monitoring`);
    return response.data;
  },

  async completeMedicationMonitoring(patientId: number): Promise<MedicationMonitoringReview> {
    const response = await api.post(`/patients/${patientId}/medication-monitoring/done`);
    return response.data;
  },
};

// Option slugs must match app/services/titration_service.py on the backend.
export const OTHER_OPTION = 'other';

export const TITRATION_REJECT_REASONS: { value: string; label: string }[] = [
  { value: 'clinically_concerning', label: 'Clinically concerning measurements' },
  { value: 'not_clinically_appropriate', label: 'Treatment not clinically appropriate at this time' },
  { value: OTHER_OPTION, label: 'Other' },
];

export const TITRATION_REQUEST_INFO_OPTIONS: { value: string; label: string }[] = [
  { value: 'repeat_bp', label: 'Repeat BP measurement' },
  { value: 'repeat_heart_rate', label: 'Repeat heart rate' },
  { value: 'updated_weight', label: 'Updated weight' },
  { value: 'ecg_required', label: 'ECG required' },
  { value: 'blood_tests_required', label: 'Blood tests required' },
  { value: 'gp_medical_info_required', label: 'GP medical information required' },
  { value: OTHER_OPTION, label: 'Other' },
];

// Treatment decision reasons, recorded on the Treatment tab of a titration follow-up.
export const STOP_TREATMENT_REASON_OPTIONS: { value: string; label: string }[] = [
  { value: 'severe_side_effects', label: 'Severe side effects' },
  { value: 'blood_pressure_concerns', label: 'Blood pressure concerns' },
  { value: 'heart_rate_concerns', label: 'Heart rate concerns' },
  { value: 'significant_weight_loss', label: 'Significant weight loss' },
  { value: 'medication_not_effective', label: 'Medication not effective' },
  { value: 'unable_to_tolerate_medication', label: 'Patient unable to tolerate medication' },
  { value: 'patient_chose_to_stop', label: 'Patient chose to stop treatment' },
  { value: 'non_adherence', label: 'Non-adherence to treatment' },
  { value: 'safety_concerns_identified', label: 'Safety concerns identified' },
  { value: 'referred_for_clinical_review', label: 'Referred for additional clinical review' },
  { value: OTHER_OPTION, label: 'Other' },
];

export const ADDITIONAL_REVIEW_REASON_OPTIONS: { value: string; label: string }[] = [
  { value: 'ecg_required', label: 'ECG required' },
  { value: 'blood_tests_required', label: 'Blood tests required' },
  { value: 'gp_medical_info_required', label: 'GP medical information required' },
  { value: 'psychiatric_history_clarification', label: 'Psychiatric history clarification required' },
  { value: 'other_specialist_opinion', label: 'Other specialist opinion required' },
  { value: 'additional_vitals_monitoring', label: 'Additional vitals monitoring required' },
  { value: 'missing_monitoring_data', label: 'Missing monitoring data' },
  { value: OTHER_OPTION, label: 'Other' },
];

export interface TitrationReviewResult {
  titration_measurement_status: string | null;
  titration_measurement_approval_status: string | null;
  titration_measurement_reject_reason: string[] | null;
  titration_measurement_reject_reason_text: string | null;
  titration_measurement_request_additional_info: string[] | null;
  titration_measurement_request_additional_info_text: string | null;
}

export interface PatientSummaryItem {
  meeting_index: number | null;
  date: string;
  name: string;
  meeting_type_name: string | null;
  content: string;
  meeting_id: number | null;
}

export interface TitrationMeasurement {
  questionnaire_id: number;
  questionnaire_title: string | null;
  /** True for a titration eligibility measurement (the baseline), false for the
   *  weekly monitoring submissions that reuse the same measurement fields. */
  is_baseline: boolean;
  date: string | null;
  systolic: number | null;
  diastolic: number | null;
  heart_rate: number | null;
  height: number | null;
  weight: number | null;
  bmi: number | null;
  out_of_range: string[];
  /** question_name values that failed validation (plus "bmi", which is derived),
   *  matched by name the same way flagged questionnaire answers are. */
  out_of_range_fields: string[];
  /** Medication-monitoring red flags raised by this submission. Distinct from
   *  out_of_range_fields: a week can raise a flag ("patient has concerns") with
   *  every vital perfectly normal, which is why the row needs its own marker. */
  monitoring_flags?: { code: string; label: string }[];
}

export interface MedicationMonitoringFlag {
  code: string;
  label: string;
  question_name: string;
  question_text: string | null;
  answer: string[];
}

export interface MedicationMonitoringAnswer {
  order: number | null;
  question_name: string;
  question_text: string | null;
  answer: string[];
  flagged: boolean;
}

export interface MedicationMonitoringReview {
  status: string | null;
  patient_id: number;
  patient_name: string;
  hm_id: string | null;
  id_number: string | null;
  phone: string | null;
  current_medication: string;
  prescribing_clinician: string | null;
  prescribing_clinician_id: number | null;
  submitted_at: string | null;
  questionnaire_id: number | null;
  questionnaire_title: string | null;
  flags: MedicationMonitoringFlag[];
  answers: MedicationMonitoringAnswer[];
}
